"use client"

import type { PointerEvent as ReactPointerEvent } from "react"
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react"

export type SignatureCanvasHandle = {
  clear: () => void
  toDataURL: () => string | null
}

type SignatureCanvasAreaProps = {
  error?: string
  hide?: boolean
  width?: number
  onBegin: () => void
  onClear: () => void
}

export const SignatureCanvasArea = forwardRef<SignatureCanvasHandle, SignatureCanvasAreaProps>(
  ({ error, hide, width, onBegin, onClear }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const drawingRef = useRef(false)
    const didDrawRef = useRef(false)
    const lastPointRef = useRef<{ x: number; y: number } | null>(null)

    const prepareCanvas = () => {
      const canvas = canvasRef.current
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      if (!rect.width || !rect.height) return

      const ratio = window.devicePixelRatio || 1
      canvas.width = Math.round(rect.width * ratio)
      canvas.height = Math.round(rect.height * ratio)

      const context = canvas.getContext("2d")
      if (!context) return

      context.setTransform(ratio, 0, 0, ratio, 0, 0)
      context.lineCap = "round"
      context.lineJoin = "round"
      context.lineWidth = 2
      context.strokeStyle = "#242f3e"
    }

    const clear = () => {
      const canvas = canvasRef.current
      const context = canvas?.getContext("2d")
      if (!canvas || !context) return

      context.clearRect(0, 0, canvas.width, canvas.height)
      didDrawRef.current = false
      onClear()
    }

    const getPoint = (event: ReactPointerEvent<HTMLCanvasElement>) => {
      const rect = canvasRef.current?.getBoundingClientRect()
      return {
        x: event.clientX - (rect?.left || 0),
        y: event.clientY - (rect?.top || 0),
      }
    }

    const startDrawing = (event: ReactPointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current
      const context = canvas?.getContext("2d")
      if (!canvas || !context) return

      event.preventDefault()
      canvas.setPointerCapture(event.pointerId)
      drawingRef.current = true
      const point = getPoint(event)
      lastPointRef.current = point
      context.beginPath()
      context.moveTo(point.x, point.y)
    }

    const draw = (event: ReactPointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current) return

      const context = canvasRef.current?.getContext("2d")
      const lastPoint = lastPointRef.current
      if (!context || !lastPoint) return

      event.preventDefault()
      const point = getPoint(event)
      context.beginPath()
      context.moveTo(lastPoint.x, lastPoint.y)
      context.lineTo(point.x, point.y)
      context.stroke()
      lastPointRef.current = point

      if (!didDrawRef.current) {
        didDrawRef.current = true
        onBegin()
      }
    }

    const stopDrawing = (event: ReactPointerEvent<HTMLCanvasElement>) => {
      drawingRef.current = false
      lastPointRef.current = null

      if (canvasRef.current?.hasPointerCapture(event.pointerId)) {
        canvasRef.current.releasePointerCapture(event.pointerId)
      }
    }

    useImperativeHandle(ref, () => ({
      clear,
      toDataURL: () => {
        if (!didDrawRef.current) return null
        return canvasRef.current?.toDataURL("image/png") || null
      },
    }))

    useEffect(() => {
      prepareCanvas()
    }, [hide, width])

    if (hide) return null

    return (
      <div className="tdf-signature-pad" style={width ? { maxWidth: width } : undefined}>
        <canvas
          ref={canvasRef}
          className="tdf-signature-canvas"
          aria-label="Signature"
          onPointerDown={startDrawing}
          onPointerMove={draw}
          onPointerUp={stopDrawing}
          onPointerCancel={stopDrawing}
          onPointerLeave={stopDrawing}
        />
        <div className="tdf-signature-footer">
          <p>Draw your signature on the white board above</p>
          <button className="tdf-signature-clear" type="button" onClick={clear}>
            Clear
          </button>
        </div>
        {error && <p className="tdf-field-error">{error}</p>}
      </div>
    )
  },
)

SignatureCanvasArea.displayName = "SignatureCanvasArea"
